/**
 * Ein-Klick-Schichtplan (U-32c): DER Hauptweg der Schichtplanung in 3 Schritten —
 *  1. „Zeitraum": Schichtmodell + Jahr + KW-Bereich (Muster GeneratorDialog)
 *  2. „Vorschau": GET /week-plans/preview (read-only) — Rotations-Matrix
 *     KW × Schicht = Team, bereits generierte Zellen gedimmt („vorhanden"),
 *     Zähl-Prognose + Warnungen (Teams ohne Besetzung). Fehlt der Rotations-
 *     Anker (G-17a), wird ZUERST der StartAssignmentSheet vorgeschaltet.
 *  3. „Ergebnis": POST /shift-plan/create (Phase 1 + Phase 2 in EINER
 *     Transaktion, idempotent G-23) — Zahlen beider Phasen + Warnungen,
 *     Absprung „Zum Planraster".
 *
 * Gate: personal_schreiben (durchgesetzt serverseitig).
 */

import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertTriangle, CheckCircle2 } from "lucide-react"
import { toast } from "sonner"
import { StartAssignmentSheet } from "@/components/planning/StartAssignmentSheet"
import { LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SHEET_WIDTH,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { isoWeekOf, todayIso, weeksInIsoYear } from "@/lib/isoWeek"
import { cn } from "@/lib/utils"
import { api, apiError } from "@/services/api"
import type { ShiftPlanCreateResponse, ShiftPlanPreview } from "@/types"

type Step = "form" | "preview" | "result"

interface CreateShiftPlanSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** „Zum Planraster"-Absprung im Ergebnis-Schritt (Tab-Wechsel). */
  onGoToGrid?: () => void
}

/** Gelbe Warnbox (teamsWithoutMembers, warnings) */
function WarningBox({ lines }: { lines: string[] }) {
  if (lines.length === 0) return null
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-400/50 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      <div className="space-y-1">
        {lines.map((l) => (
          <p key={l}>{l}</p>
        ))}
      </div>
    </div>
  )
}

export function CreateShiftPlanSheet({
  open,
  onOpenChange,
  onGoToGrid,
}: CreateShiftPlanSheetProps) {
  const queryClient = useQueryClient()
  const current = isoWeekOf(todayIso())
  const [shiftModelId, setShiftModelId] = useState("")
  const [year, setYear] = useState(String(current.year))
  const [fromWeek, setFromWeek] = useState(String(current.week))
  const [toWeek, setToWeek] = useState(String(current.week))
  const [step, setStep] = useState<Step>("form")
  const [result, setResult] = useState<ShiftPlanCreateResponse | null>(null)
  const [startOpen, setStartOpen] = useState(false)

  useEffect(() => {
    if (open) {
      setStep("form")
      setResult(null)
    }
  }, [open])

  const models = useQuery({
    queryKey: ["shiftModels", { size: 100 }],
    queryFn: () => api.organization.shiftModels.list({ size: 100 }),
    enabled: open,
  })
  const modelName = (models.data?.data ?? []).find((m) => m.id === shiftModelId)?.name

  // Rotations-Status (G-17a): fehlt der Anker, wird die Startbelegung vorgeschaltet.
  const rotation = useQuery({
    queryKey: ["rotationStatus", Number(shiftModelId)],
    queryFn: () => api.planning.rotation.status(Number(shiftModelId)),
    enabled: open && shiftModelId !== "",
  })
  const status = rotation.data

  const maxWeek = weeksInIsoYear(Number(year) || current.year)
  const valid =
    shiftModelId !== "" &&
    Number(year) >= 2000 &&
    Number(fromWeek) >= 1 &&
    Number(toWeek) <= maxWeek &&
    Number(fromWeek) <= Number(toWeek)

  const params = {
    shiftModelId: Number(shiftModelId),
    year: Number(year),
    fromWeek: Number(fromWeek),
    toWeek: Number(toWeek),
  }

  // Read-only-Vorschau — nur im Vorschau-Schritt aktiv, speichert nichts.
  const preview = useQuery({
    queryKey: ["shiftPlanPreview", params],
    queryFn: () => api.planning.preview(params),
    enabled: open && step === "preview" && valid,
  })

  /** „Vorschau": fehlt der Anker, erst Startbelegung erfragen (G-17a). */
  function onPreviewClick() {
    if (status && !status.hasAnchor) {
      setStartOpen(true)
      return
    }
    setStep("preview")
  }

  const create = useMutation({
    mutationFn: () => api.planning.createShiftPlan(params),
    onSuccess: (r) => {
      setResult(r)
      setStep("result")
      queryClient.invalidateQueries({ queryKey: ["weekPlans"] })
      queryClient.invalidateQueries({ queryKey: ["shiftPlan"] })
      queryClient.invalidateQueries({ queryKey: ["rotationStatus"] })
      toast.success(
        `Schichtplan erstellt: ${r.weekPlans.created} Wochenpläne + ${r.entries.created} Tageszeilen`
      )
    },
    onError: (e) => toast.error(apiError(e)),
  })

  // --- Vorschau-Matrix: Zeilen = KWs, Spalten = Schichten (Rotationsreihenfolge) ---
  const matrix: ShiftPlanPreview["matrix"] = useMemo(
    () => preview.data?.matrix ?? [],
    [preview.data]
  )
  const shiftCols = useMemo(() => {
    const map = new Map<string, string>()
    for (const c of matrix) if (!map.has(c.shiftId)) map.set(c.shiftId, c.shiftName)
    return [...map.entries()]
  }, [matrix])
  const weekRows = useMemo(() => {
    const seen = new Map<string, { year: number; week: number }>()
    for (const c of matrix) seen.set(`${c.year}-${c.week}`, { year: c.year, week: c.week })
    return [...seen.values()]
  }, [matrix])
  const cellAt = (week: number, shiftId: string) =>
    matrix.find((c) => c.week === week && c.shiftId === shiftId)

  const warnLines = useMemo(() => {
    const p = preview.data
    if (!p) return []
    const lines: string[] = []
    if (p.teamsWithoutMembers.length > 0)
      lines.push(
        `Teams ohne Besetzung: ${p.teamsWithoutMembers.join(", ")} — für diese Teams entstehen KEINE Tageszeilen. Team-Besetzung in den Stammdaten pflegen und erneut erstellen (idempotent).`
      )
    return lines
  }, [preview.data])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="create-shift-plan" defaultWidth={step === "preview" ? SHEET_WIDTH.xl : SHEET_WIDTH.md} className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {step === "form" && "Schichtplan erstellen — Zeitraum"}
            {step === "preview" && "Schichtplan erstellen — Vorschau"}
            {step === "result" && "Schichtplan erstellt"}
          </SheetTitle>
          <SheetDescription>
            {step === "form" &&
              "Ein Schritt statt zwei Phasen: Wochenplan (Rotation) und Tages-Planzeilen werden zusammen erstellt — nach einer Vorschau."}
            {step === "preview" &&
              "Noch ist nichts gespeichert. Prüfen Sie die Rotations-Matrix und die Prognose, dann „Erstellen“."}
            {step === "result" &&
              "Wochenplan und Tagesplan wurden in einer Transaktion erstellt (idempotent — kein Duplikat bei Wiederholung)."}
          </SheetDescription>
        </SheetHeader>

        {/* ------------------------- Schritt 1: Zeitraum ------------------------- */}
        {step === "form" && (
          <div className="grid gap-4 py-2">
            <div className="space-y-1.5">
              <Label>Schichtmodell *</Label>
              <Select value={shiftModelId} onChange={(e) => setShiftModelId(e.target.value)}>
                <option value="">— Schichtmodell wählen —</option>
                {(models.data?.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.shiftType})
                  </option>
                ))}
              </Select>
            </div>

            {shiftModelId !== "" && rotation.isSuccess && status && !status.hasAnchor && (
              <div className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
                Dieser Schichtplan wird zum ersten Mal gestartet — vor der Vorschau wird
                die Startbelegung erfragt (welches Team aktuell in welcher Schicht
                arbeitet).
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Jahr *</Label>
                <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>KW von *</Label>
                <Input
                  type="number"
                  min={1}
                  max={maxWeek}
                  value={fromWeek}
                  onChange={(e) => setFromWeek(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>KW bis *</Label>
                <Input
                  type="number"
                  min={1}
                  max={maxWeek}
                  value={toWeek}
                  onChange={(e) => setToWeek(e.target.value)}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {year || "—"} hat {maxWeek} ISO-Kalenderwochen.
            </p>
          </div>
        )}

        {/* ------------------------- Schritt 2: Vorschau ------------------------- */}
        {step === "preview" && (
          <div className="grid gap-4 py-2">
            {preview.isLoading && <LoadingSpinner />}
            {preview.isError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {apiError(preview.error)}
              </div>
            )}
            {preview.isSuccess && preview.data && (
              <>
                {/* Anker nachträglich verschwunden/nie gesetzt → Startbelegung anbieten */}
                {!preview.data.hasAnchor && status && (
                  <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-400/50 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
                    <span>
                      Noch keine Startbelegung gesetzt — die Matrix zeigt die natürliche
                      Phase ab KW {fromWeek}.
                    </span>
                    <Button size="sm" variant="outline" onClick={() => setStartOpen(true)}>
                      Startbelegung festlegen …
                    </Button>
                  </div>
                )}

                <div className="max-h-72 overflow-y-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-24">KW</TableHead>
                        {shiftCols.map(([id, name]) => (
                          <TableHead key={id}>{name}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {weekRows.map((w) => (
                        <TableRow key={`${w.year}-${w.week}`}>
                          <TableCell className="font-medium tabular-nums">
                            KW {w.week}/{w.year}
                          </TableCell>
                          {shiftCols.map(([id]) => {
                            const cell = cellAt(w.week, id)
                            return (
                              <TableCell
                                key={id}
                                className={cn(cell?.exists && "text-muted-foreground/60")}
                              >
                                <span className="inline-flex items-center gap-1.5">
                                  {cell?.teamName ?? "—"}
                                  {cell?.exists && (
                                    <Badge
                                      variant="outline"
                                      className="px-1 py-0 text-[10px] text-muted-foreground"
                                    >
                                      vorhanden
                                    </Badge>
                                  )}
                                </span>
                              </TableCell>
                            )
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Zähl-Prognose */}
                <p className="text-sm">
                  <span className="font-medium">
                    {preview.data.wouldCreateWeekPlans} Wochenpläne +{" "}
                    {preview.data.wouldCreateEntries} Tageszeilen werden erstellt
                  </span>{" "}
                  <span className="text-muted-foreground">
                    ({preview.data.skippedExistingWeekPlans} bereits vorhanden — werden
                    übersprungen)
                  </span>
                </p>

                <WarningBox lines={warnLines} />
              </>
            )}
          </div>
        )}

        {/* ------------------------- Schritt 3: Ergebnis ------------------------- */}
        {step === "result" && result && (
          <div className="grid gap-4 py-2">
            <div className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-green-600" />
              <div className="space-y-1">
                <p>
                  <span className="font-medium">Wochenplan:</span> {result.weekPlans.created}{" "}
                  Zeilen neu · {result.weekPlans.skippedExisting} übersprungen ·{" "}
                  {result.weekPlans.weeks} KW × {result.weekPlans.shifts} Schichten,{" "}
                  {result.weekPlans.teams} Teams in Rotation
                </p>
                <p>
                  <span className="font-medium">Tagesplan:</span> {result.entries.created}{" "}
                  Planzeilen neu · {result.entries.skippedExisting} übersprungen · aus{" "}
                  {result.entries.weekPlans} Wochenplan-Zeilen
                </p>
              </div>
            </div>
            <WarningBox lines={result.warnings} />
          </div>
        )}

        <SheetFooter>
          {step === "form" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button
                disabled={!valid || (shiftModelId !== "" && rotation.isLoading)}
                onClick={onPreviewClick}
              >
                Vorschau
              </Button>
            </>
          )}
          {step === "preview" && (
            <>
              <Button variant="outline" onClick={() => setStep("form")}>
                Zurück
              </Button>
              <Button
                disabled={!preview.isSuccess || create.isPending}
                onClick={() => create.mutate()}
              >
                Erstellen
              </Button>
            </>
          )}
          {step === "result" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Schließen
              </Button>
              {onGoToGrid && (
                <Button
                  onClick={() => {
                    onOpenChange(false)
                    onGoToGrid()
                  }}
                >
                  Zum Planraster
                </Button>
              )}
            </>
          )}
        </SheetFooter>
      </SheetContent>

      {/* Startbelegung erfragen (G-17a) — danach direkt in die Vorschau. */}
      {status && !status.hasAnchor && (
        <StartAssignmentSheet
          open={startOpen}
          onOpenChange={setStartOpen}
          modelId={Number(shiftModelId)}
          modelName={modelName}
          status={status}
          onAnchorSet={() => {
            setStartOpen(false)
            queryClient.invalidateQueries({ queryKey: ["shiftPlanPreview"] })
            setStep("preview")
          }}
        />
      )}
    </Sheet>
  )
}
